import Button from "@/components/Button";
import ListItem from "@/components/ListItem/ListItem";
import SubSectionHeading from "@/components/SubSectionHeading/SubSectionHeading";
import { SkeletonElement, ListItemSkeleton } from "@/components/Skeleton";
import type { Person } from "../CreateGlobalInteractionSideSheet";

interface PeopleSelectionListsProps {
  selectedPeople: Person[];
  availablePeople: Person[];
  loading?: boolean;
  onSelect: (person: Person) => void;
  onDeselect: (person: Person) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export default function PeopleSelectionLists({
  selectedPeople,
  availablePeople,
  loading,
  onSelect,
  onDeselect,
  onSelectAll,
  onDeselectAll
}: PeopleSelectionListsProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        <SkeletonElement width="w-1/4" height="h-5" />
        <ListItemSkeleton rows={3} />
      </div>
    );
  }

  return (
    <>
      {selectedPeople.length > 0 && (
        <div className="sticky -top-px z-10 pt-px pb-2 bg-sys-brand-surface-container-low flex items-center justify-between">
          <SubSectionHeading>Selected ({selectedPeople.length})</SubSectionHeading>
          <Button variant="text" onClick={onDeselectAll}>
            Deselect All
          </Button>
        </div>
      )}

      {selectedPeople.length > 0 && (
        <div className="!mt-0 space-y-1">
          {selectedPeople
            .sort((a, b) => b.index - a.index)
            .map((person) => (
              <ListItem
                key={person.id}
                compact
                useAvatar
                picture={person.profilePhoto}
                headline={`${person.firstName} ${person.lastName}`}
                supportingText={[person.facilityName, person.sectionName].filter(Boolean).join("\n") || undefined}
                useCheckbox
                checkboxChecked={true}
                onCheckboxChange={() => onDeselect(person)}
                checkboxName={`person-${person.id}-selected`}
              />
            ))}
        </div>
      )}

      {availablePeople.length > 0 && (
        <div className="flex items-center justify-between mb-2">
          <SubSectionHeading>Available</SubSectionHeading>
          <Button variant="text" onClick={onSelectAll}>
            Select All
          </Button>
        </div>
      )}

      {availablePeople.length > 0 && (
        <div className="!mt-0 space-y-1">
          {availablePeople
            .sort((a, b) => b.index - a.index)
            .map((person) => (
              <ListItem
                key={person.id}
                compact
                useAvatar
                picture={person.profilePhoto}
                headline={`${person.firstName} ${person.lastName}`}
                supportingText={[person.facilityName, person.sectionName].filter(Boolean).join("\n") || undefined}
                useCheckbox
                checkboxChecked={false}
                onCheckboxChange={() => onSelect(person)}
                checkboxName={`person-${person.id}-available`}
              />
            ))}
        </div>
      )}
    </>
  );
}
